/**
 * The invoice ledger behind AR Reminders.
 *
 * Two things were missing and between them they stopped AR from ever closing:
 *
 *   - There was no way in the entire application to mark an invoice as paid.
 *     `invoiceService.recordPayment` existed and nothing called it, so
 *     `payment_received_at` was never written, "AR Paid" never happened, the
 *     AR-cycle metric on the sales report was permanently null, and the
 *     commission that becomes payable at AR Paid never did.
 *   - Generated invoices sat at `status = 'draft'` because `markAsSent` only
 *     ran when a buyer email existed and no screen captured one. The AR view
 *     reads sent/overdue/disputed, so drafts were invisible — the work looked
 *     done when nothing had been issued.
 */

import { supabase } from '../lib/supabase'
import { emailService } from './emailService'
import { invoiceService } from './invoiceService'
import { smsService } from './smsService'
import { recalculateRisk, setCreditStatus } from './buyerService'
import { lockCommission } from './saleService'

export type InvoiceState = 'draft' | 'sent' | 'overdue' | 'paid' | 'partial' | 'disputed' | 'cancelled'
const orNull = (v: unknown): number | null =>
  v === null || v === undefined || v === '' ? null : Number(v)

export interface LedgerInvoice {
  id: string
  dealId: string
  invoiceNumber: string
  buyerName: string
  buyerEmail: string
  buyerMobile: string
  totalAmount: number
  /** Recorded only on invoices raised since the invoice maths was corrected. */
  netPrice: number | null
  gstAmount: number | null
  discount: number | null
  taxRate: number | null
  amountReceived: number
  outstanding: number
  invoiceDate: string
  dueDate: string
  status: string
  paymentReceivedAt: string | null
  paymentReference: string
  daysOverdue: number
  vehicle: string
  vin: string
  dealStatus: string
  publicToken: string
  linkSmsSentAt: string | null
  /** Blocked from being issued because there is nobody to send it to. */
  blockedNoEmail: boolean
}

export interface RecordPaymentInput {
  invoiceId: string
  amount: number
  receivedOn: string
  method: string
  reference?: string
  notes?: string
  userId: string
}

const daysBetween = (iso: string) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)

const vehicleLabel = (d: any) =>
  [d?.autograb_year ?? d?.vehicle_year, d?.autograb_make || d?.vehicle_make, d?.autograb_model || d?.vehicle_model]
    .filter(Boolean)
    .join(' ')
    .trim() || '—'

export async function loadInvoiceLedger(): Promise<LedgerInvoice[]> {
  const [inv, deals] = await Promise.all([
    supabase.from('invoices').select('*').order('invoice_date', { ascending: false }),
    supabase.from('deals').select('*').is('deleted_at', null),
  ])
  if (inv.error) throw inv.error
  if (deals.error) throw deals.error

  // No embedded relations in .select() strings in this codebase — an unverifiable
  // embed resolves to GenericStringError and poisons the whole row type, which
  // has broken the build here before. Join in code instead.
  const dealById = new Map((deals.data || []).map((d: any) => [d.id, d]))

  /*
   * An invoice whose deal has been archived drops out of the ledger, the same as
   * it now drops out of the reminder ladder. Archiving is how a vehicle leaves
   * the book, and an invoice sitting in Outstanding for a car that appears
   * nowhere else is a number nobody can act on.
   */
  return (inv.data || [])
    .filter((i: any) => !i.deal_id || dealById.has(i.deal_id))
    .map((i: any): LedgerInvoice => {
    const deal = dealById.get(i.deal_id)
    const total = Number(i.total_amount) || 0
    const received = Number(i.payment_amount_received) || 0
    return {
      id: i.id,
      dealId: i.deal_id,
      invoiceNumber: i.invoice_number,
      buyerName: i.buyer_name || deal?.buyer_name || '',
      buyerEmail: i.buyer_email || deal?.buyer_email || '',
      buyerMobile: i.buyer_mobile || deal?.buyer_mobile || '',
      totalAmount: total,
      /**
       * Null on an invoice raised before the tax-invoice maths was fixed, and shown as a dash
       * rather than as zero. A legacy invoice genuinely does not record these; printing
       * $0.00 net and $0.00 GST against a real total would be a worse lie than a blank.
       */
      netPrice: orNull(i.net_price),
      gstAmount: orNull(i.gst_amount),
      discount: orNull(i.discount_amount),
      taxRate: orNull(i.tax_rate),
      amountReceived: received,
      outstanding: Math.max(0, total - received),
      invoiceDate: i.invoice_date,
      dueDate: i.due_date,
      status: i.status,
      paymentReceivedAt: i.payment_received_at || null,
      paymentReference: i.payment_reference || '',
      daysOverdue: i.due_date ? daysBetween(i.due_date) : 0,
      vehicle: vehicleLabel(deal),
      vin: deal?.autograb_vin || deal?.vehicle_vin || '',
      dealStatus: deal?.deal_status || '',
      publicToken: i.public_token || '',
      linkSmsSentAt: i.link_sms_sent_at || null,
      blockedNoEmail: i.status === 'draft' && !(i.buyer_email || deal?.buyer_email),
    }
  })
}

/**
 * Record money received against an invoice.
 *
 * Partial payments keep the invoice open rather than silently marking it paid,
 * and the receipt carries who recorded it, when the money actually landed, and
 * the bank reference — none of which the original function had anywhere to put.
 */
export async function recordPayment(input: RecordPaymentInput): Promise<{ paidInFull: boolean; message: string }> {
  const { data: invoice, error: readError } = await supabase
    .from('invoices')
    .select('total_amount, payment_amount_received, invoice_number, deal_id, status')
    .eq('id', input.invoiceId)
    .single()
  if (readError) throw readError

  const total = Number(invoice.total_amount) || 0
  const alreadyReceived = Number(invoice.payment_amount_received) || 0
  const cumulative = Math.round((alreadyReceived + input.amount) * 100) / 100

  // A cent of float drift should not stop an invoice closing.
  const paidInFull = cumulative + 0.005 >= total

  const { error } = await supabase
    .from('invoices')
    .update({
      payment_received_at: new Date(`${input.receivedOn}T00:00:00`).toISOString(),
      payment_amount_received: cumulative,
      payment_reference: input.reference?.trim() || null,
      payment_method: input.method,
      payment_recorded_by: input.userId,
      payment_notes: input.notes?.trim() || null,
      status: paidInFull ? 'paid' : 'partial',
      updated_at: new Date().toISOString(),
    })
    .eq('id', input.invoiceId)
  if (error) throw error

  // §5.4: the risk rating is recalculated after every payment event. A rating
  // that only refreshes when someone opens a report is a rating nobody trusts.
  // Neither of these should be able to fail the payment itself.
  let riskNote = ''
  try {
    const { data: full } = await supabase
      .from('invoices')
      .select('buyer_id, deal_id')
      .eq('id', input.invoiceId)
      .maybeSingle()

    const buyerId =
      (full as any)?.buyer_id ??
      (
        await supabase
          .from('deals')
          .select('buyer_id')
          .eq('id', (full as any)?.deal_id || invoice.deal_id)
          .maybeSingle()
      ).data?.buyer_id

    if (buyerId) {
      const assessment = await recalculateRisk(buyerId)
      // Paying in full is what lifts a suspension — the ladder put it there.
      if (paidInFull) {
        const { data: stillOwing } = await supabase
          .from('invoices')
          .select('id')
          .eq('buyer_id', buyerId)
          .not('status', 'in', '("paid","cancelled","draft")')
          .limit(1)
        if (!(stillOwing || []).length) {
          await setCreditStatus(buyerId, 'ok', 'All invoices settled.')
          riskNote = ' Credit restored — nothing outstanding.'
        }
      }
      if (assessment.rating === 'high') {
        riskNote += ' This buyer is now rated high risk.'
      }
    }
  } catch {
    // A rating is a convenience; failing to update it must not lose a payment.
  }

  // §6.1: an expense added before the invoice is paid re-opens the locked
  // commission. Once it is paid the figure stands, so this is the moment to
  // stop allowing that.
  if (paidInFull && invoice.deal_id) {
    await supabase
      .from('deals')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', invoice.deal_id)
  }

  return {
    paidInFull,
    message:
      (paidInFull
        ? `Invoice ${invoice.invoice_number} is paid in full.`
        : `Part payment recorded. $${(total - cumulative).toLocaleString('en-AU', { minimumFractionDigits: 2 })} still outstanding on ${invoice.invoice_number}.`) + riskNote,
  }
}

/**
 * Issue a draft invoice to the buyer.
 *
 * Never trust `res.ok` alone — the same rule the PPSR and SMS work established.
 * The invoice is only stamped as sent once the provider confirms, so a failure
 * leaves it as a draft that can be retried rather than a "sent" invoice nobody
 * ever received.
 */
export async function issueInvoice(invoiceId: string): Promise<{ ok: boolean; message: string }> {
  const { data: invoice, error } = await supabase
    .from('invoices')
    .select('id, invoice_number, buyer_name, buyer_email, total_amount, due_date, status, deal_id')
    .eq('id', invoiceId)
    .single()
  if (error) throw error

  if (!invoice.buyer_email) {
    return {
      ok: false,
      message: 'No buyer email on this invoice. Add the buyer details on the deal first.',
    }
  }

  const sent = await emailService.sendInvoice(
    invoice.buyer_email,
    invoice.buyer_name || 'Buyer',
    invoice.invoice_number,
    Number(invoice.total_amount) || 0,
    invoice.due_date
  )

  if (!sent?.success) {
    return { ok: false, message: sent?.message || 'The invoice email could not be sent — nothing was changed.' }
  }

  const marked = await invoiceService.markAsSent(invoiceId)
  if (!marked.success) return { ok: false, message: marked.message }

  // §5.3: issuing the tax invoice is what puts the deal into "Sold — Awaiting
  // Payment", and it is the moment commission is locked. Both are done after
  // the send is confirmed, so a failed email leaves nothing half-applied.
  let extra = ''
  try {
    const { data: deal } = await supabase
      .from('deals')
      .select('id, deal_status')
      .eq('id', (invoice as any).deal_id)
      .maybeSingle()

    if (deal) {
      const net = await lockCommission(deal.id)
      extra = ` Commission locked against a net profit of $${net.toLocaleString('en-AU')}.`

    /* Until 10 Sep, issuing the invoice also moved the deal to "Sold — Awaiting Payment".
       Winnie retired that stage: *"Sold-awaiting for payment is no longer required in the
       deal pipeline as both SOLD-in transit and Sold-Delivered will be captured in the
       Sale (AR) invoice report as well as the Debtor report."*

       So the deal stays where the sales rep put it, and where the money has got to is
       read from the invoice. Moving it here would now push the car onto a status with no
       column, and it would simply vanish off the board the moment it was invoiced. */
    }
  } catch {
    // The invoice is sent either way; the status move is recoverable by hand.
  }

  return {
    ok: true,
    message: `Invoice ${invoice.invoice_number} sent to ${invoice.buyer_email}.${extra}`,
  }
}

/**
 * Text the buyer a link to their invoice.
 *
 * The guard column is stamped only after the provider confirms, so a failed
 * send can be retried rather than being recorded as sent — the same rule the
 * AR SMS ladder follows.
 */
export async function sendInvoiceLink(invoiceId: string): Promise<{ ok: boolean; message: string }> {
  const { data: inv, error } = await supabase
    .from('invoices')
    .select('id, invoice_number, buyer_name, buyer_mobile, total_amount, due_date, public_token, deal_id')
    .eq('id', invoiceId)
    .single()
  if (error) {
    // 42703 = column does not exist. Until migration 022 has been run there is
    // no public_token to build a link from, and the raw PostgREST message means
    // nothing to whoever clicked the button.
    if ((error as any).code === '42703' || /public_token/.test(error.message || '')) {
      return { ok: false, message: 'Invoice links are not switched on yet — migration 022 has not been run on this environment.' }
    }
    throw error
  }

  let mobile = inv.buyer_mobile as string | null
  if (!mobile) {
    // Fall back to the deal, for invoices generated before the buyer form existed.
    const { data: deal } = await supabase
      .from('deals')
      .select('buyer_mobile')
      .eq('id', inv.deal_id)
      .maybeSingle()
    mobile = (deal as any)?.buyer_mobile || null
  }

  if (!mobile) {
    return { ok: false, message: 'No buyer mobile on file. Add it under Buyer Details on the deal.' }
  }
  if (!inv.public_token) {
    return { ok: false, message: 'This invoice has no share token yet — run migration 022.' }
  }

  const res = await smsService.sendInvoiceLinkSMS(
    mobile,
    inv.buyer_name || '',
    inv.invoice_number,
    Number(inv.total_amount) || 0,
    inv.due_date,
    inv.public_token,
    inv.id,
    inv.deal_id
  )

  if (!res?.success) {
    return { ok: false, message: res?.message || 'The SMS could not be sent — nothing was changed.' }
  }

  await supabase
    .from('invoices')
    .update({ link_sms_sent_at: new Date().toISOString() })
    .eq('id', invoiceId)

  return { ok: true, message: `Invoice link texted to ${mobile}.` }
}

export const INVOICE_STATE_LABEL: Record<string, string> = {
  draft: 'Draft — not issued',
  sent: 'Issued',
  partial: 'Part paid',
  overdue: 'Overdue',
  paid: 'Paid',
  disputed: 'Disputed',
  cancelled: 'Cancelled',
}
